import { IsoCredAd } from "@/components/IsoCredAd";
import { RequirePhone } from "@/components/RequirePhone";
import { ThemeToggle } from "@/components/Theme";
import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  Home, Search, CalendarDays, FolderHeart, User, LogOut, Bell, LayoutDashboard, Clock, FileText, Wallet, Stethoscope,
  Users, Receipt, Settings, ScrollText, LifeBuoy, ShieldCheck, Menu, Store, ShoppingBag, Activity, Star, Microscope, UsersRound, ChevronDown, MessageSquareText } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/common";
import { RouteFade } from "@/components/Extras";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDT, actingAs, setActingAs } from "@/lib/api";

function ProfileSwitcher() {
  const acting = actingAs();
  const [members, setMembers] = useState([]);
  useEffect(() => { if (!acting) api.get("/family").then((r) => setMembers(r.data.members.filter((m) => m.status === "active"))).catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  if (acting) return <button onClick={() => setActingAs(null)} className="rounded-full bg-[#FF9700]/15 text-[#9A5B00] px-3 py-1.5 text-xs font-semibold" data-testid="switch-back-btn">Back to my profile</button>;
  if (!members.length) return null;
  return (
    <Popover><PopoverTrigger asChild><button className="rounded-full border border-[#E2E8ED] px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid="profile-switcher"><Users className="h-3.5 w-3.5" />Family</button></PopoverTrigger>
      <PopoverContent align="end" className="w-64 p-2">{members.map((m) => (
        <button key={m.id} onClick={() => setActingAs(m)} className="w-full text-left rounded-lg px-3 py-2 hover:bg-[#F7F9FA]" data-testid={`switch-to-${m.id}`}><p className="text-sm font-medium">{m.name}</p><p className="text-[11px] muted">{m.patient_code} · {m.relationship}</p></button>))}</PopoverContent></Popover>
  );
}

const NAV = {
  patient: [["Home", "/patient", Home], ["Find Doctor", "/patient/find-doctor", Search], ["Suvidha", "/patient/suvidha", Store], ["Appointments", "/patient/appointments", CalendarDays],
    ["Records", "/patient/records", FolderHeart], ["Health Trends", "/patient/trends", Activity, true], ["Orders", "/patient/orders", ShoppingBag, true], ["Family", "/patient/family", Users, true], ["Profile", "/patient/profile", User, true], ["Payments", "/patient/payments", Wallet, true], ["Help", "/help", LifeBuoy, true]],
  partner: [["Dashboard", "/partner", LayoutDashboard], ["Payouts", "/partner/earnings", Wallet], ["Business Profile", "/partner/profile", Store], ["Help", "/help", LifeBuoy]],
  doctor: [["Dashboard", "/doctor", LayoutDashboard], ["Appointments", "/doctor/appointments", CalendarDays], ["Availability", "/doctor/availability", Clock],
    ["Prescriptions", "/doctor/prescriptions", FileText], ["Earnings", "/doctor/earnings", Wallet], ["Ratings & Reviews", "/doctor/reviews", Star], ["Profile & Verification", "/doctor/profile", Stethoscope], ["Help", "/help", LifeBuoy]],
  admin: [["Overview", "/admin", LayoutDashboard], ["Doctors", "/admin/doctors", Stethoscope], ["Users", "/admin/users", Users], ["Partners", "/admin/partners", Store], ["Appointments", "/admin/appointments", CalendarDays], ["Payments & Refunds", "/admin/finance", Receipt], ["Support", "/admin/support", LifeBuoy], ["Reviews", "/admin/reviews", Star],
    ["SMS", "/admin/sms", MessageSquareText], ["Settings", "/admin/settings", Settings], ["Audit Logs", "/admin/audit", ScrollText], ["Security", "/admin/security", ShieldCheck]],
};

const slug = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

const PATIENT_GROUPS = [
  ["Consultations", Stethoscope, [["Find Doctor", "/patient/find-doctor", Search], ["Report Consult", "/patient/report-consult", Microscope], ["Joint Consult", "/patient/joint-consult", UsersRound], ["Appointments", "/patient/appointments", CalendarDays]]],
  ["Health", FolderHeart, [["Records & Nirogyam AI", "/patient/records", FolderHeart], ["Health Trends", "/patient/trends", Activity], ["Family", "/patient/family", Users]]],
  ["Suvidha Centre", Store, [["Services", "/patient/suvidha", Store], ["Orders", "/patient/orders", ShoppingBag]]],
  ["Account", User, [["Payments", "/patient/payments", Wallet], ["Profile", "/patient/profile", User], ["Help", "/help", LifeBuoy]]],
];

function PatientNav({ onClick }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(() => PATIENT_GROUPS.find(([, , items]) => items.some(([, to]) => pathname.startsWith(to)))?.[0] || "Consultations");
  return (
    <>
      <NavItems items={[["Home", "/patient", Home]]} onClick={onClick} />
      {PATIENT_GROUPS.map(([g, GIcon, items]) => (
        <div key={g}>
          <button type="button" onClick={() => setOpen(open === g ? "" : g)} aria-expanded={open === g} data-testid={`sidenav-group-${slug(g)}`}
            className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-[#17232E]/75 hover:bg-[#F3F6F8] transition-colors">
            <GIcon className="h-4 w-4" /><span className="flex-1 text-left font-medium">{g}</span><ChevronDown className={`h-4 w-4 transition-transform ${open === g ? "rotate-180" : ""}`} />
          </button>
          {open === g && <div className="ml-4 pl-2 border-l border-[#E2E8ED] space-y-0.5"><NavItems items={items} onClick={onClick} /></div>}
        </div>))}
    </>
  );
}

function NavItems({ items, onClick }) {
  return items.map(([l, to, Icon]) => (
    <NavLink key={to} to={to} end={to.split("/").length <= 2} onClick={onClick} data-testid={`sidenav-${slug(l)}`}
      className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm transition-colors ${isActive ? "bg-[#2412B5]/[0.07] text-[#2412B5] font-semibold" : "text-[#17232E]/75 hover:bg-[#F7F9FA]"}`}>
      <Icon className="h-4 w-4" /> {l}
    </NavLink>
  ));
}

function Notifications() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const nav = useNavigate();
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, []);
  return (
    <Popover onOpenChange={(o) => o && data.unread && api.post("/notifications/read-all").then(() => setData((d) => ({ ...d, unread: 0 })))}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-full hover:bg-[#F7F9FA]" data-testid="notifications-bell" aria-label="Notifications">
          <Bell className="h-5 w-5 text-[#17232E]" />
          {data.unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-[#FF9700] text-[10px] font-bold text-white flex items-center justify-center" data-testid="notifications-count">{data.unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-4 py-3 border-b text-sm font-semibold">Notifications</div>
        <div className="max-h-80 overflow-auto">
          {data.items.length === 0 && <p className="p-4 text-sm muted">You're all caught up.</p>}
          {data.items.map((n) => (
            <button key={n.id} onClick={() => n.link && nav(n.link)} className="block w-full text-left px-4 py-3 border-b last:border-0 hover:bg-[#F7F9FA]">
              <p className="text-sm font-medium">{n.title}</p>
              <p className="text-[11px] muted mt-0.5">{fmtDT(n.created_at)}</p>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function PortalLayout({ role }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const items = NAV[role];
  const [open, setOpen] = useState(false);
  const doLogout = async () => { await logout(); nav("/auth"); };
  const roleLabel = { patient: "Patient", doctor: "Doctor workspace", admin: "Administration", partner: "Partner" }[role];
  return (
    <div className="min-h-screen bg-[#F7F9FA] overflow-x-hidden">
      <aside className="hidden lg:flex fixed inset-y-0 left-0 w-64 flex-col bg-white border-r border-[#E2E8ED] px-4 py-6">
        <div className="px-2"><Logo className="h-10" to={`/${role}`} /></div>
        <p className="eyebrow px-3 mt-6 mb-2">{roleLabel}</p>
        <nav className="flex-1 space-y-1">{role === "patient" ? <PatientNav /> : <NavItems items={items} />}</nav>
        <div className="border-t border-[#E2E8ED] pt-4 px-2">
          <p className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="text-xs muted truncate">{user?.email}</p>
          {user?.patient_code && <p className="text-[11px] font-semibold text-[#2412B5] mt-1" data-testid="sidebar-patient-id">ID: {user.patient_code}</p>}
          <div className="mt-3 flex items-center justify-between"><span className="text-xs muted">Dark mode</span><ThemeToggle /></div>
          <button onClick={doLogout} data-testid="logout-btn" className="mt-3 flex items-center gap-2 text-sm text-[#657581] hover:text-red-600"><LogOut className="h-4 w-4" /> Sign out</button>
        </div>
      </aside>
      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 h-16 bg-white/90 backdrop-blur-md border-b border-[#E2E8ED] flex items-center justify-between px-4 sm:px-8">
          <div className="flex items-center gap-2 lg:hidden">
            {(

              <Sheet open={open} onOpenChange={setOpen}>
                <SheetTrigger asChild><button className="p-2" data-testid="portal-menu-toggle" aria-label="Menu"><Menu className="h-5 w-5" /></button></SheetTrigger>
                <SheetContent side="left" className="w-72 p-4">
                  <SheetTitle className="sr-only">Navigation</SheetTitle>
                  <Logo className="h-9" />
                  <nav className="mt-6 space-y-1">{role === "patient" ? <PatientNav onClick={() => setOpen(false)} /> : <NavItems items={items} onClick={() => setOpen(false)} />}</nav>
                  <div className="mt-6 flex items-center justify-between"><span className="text-sm muted">Dark mode</span><ThemeToggle /></div>
                  <button onClick={doLogout} className="mt-6 flex items-center gap-2 text-sm text-red-600"><LogOut className="h-4 w-4" /> Sign out</button>
                </SheetContent>
              </Sheet>
            )}
            <Logo className="h-8" to={`/${role}`} />
          </div>
          <p className="hidden lg:block text-sm muted">Times shown in IST (Asia/Kolkata)</p>
          <div className="flex items-center gap-1">
            {role === "patient" && <ProfileSwitcher />}
            <ThemeToggle className="lg:hidden mr-1" />
            <Notifications />
            {role === "patient" && <button onClick={doLogout} className="lg:hidden p-2" data-testid="mobile-logout-btn" aria-label="Sign out"><LogOut className="h-5 w-5 text-[#657581]" /></button>}
          </div>
        </header>
        {role === "patient" && actingAs() && <div className="bg-[#FF9700]/10 border-b border-[#FF9700]/30 px-4 sm:px-8 py-2 text-sm text-[#9A5B00]" data-testid="acting-banner">You are managing <b>{actingAs().name}</b>'s profile (ID {actingAs().patient_code}). Bookings, reports and orders are saved to their record.</div>}
        <main className="mx-auto max-w-6xl px-4 sm:px-8 py-8 pb-28 lg:pb-12"><RouteFade><Outlet /></RouteFade>{role === "partner" && <IsoCredAd />}</main>
        <RequirePhone />
      </div>
      {role === "patient" && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#E2E8ED] grid grid-cols-5" data-testid="patient-bottom-nav">
          {items.filter((i) => !i[3]).map(([l, to, Icon]) => (
            <NavLink key={to} to={to} end={to === "/patient"} data-testid={`bottomnav-${slug(l)}`}
              className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] ${isActive ? "text-[#086B2D] font-semibold" : "text-[#657581]"}`}>
              <Icon className="h-5 w-5" /> {l}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}
